import { useState, useEffect } from "react";
import { useMutation } from '@apollo/client';
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import configJson from '../../public/json/configJson.json';
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import client, { GRAPHQL_URI } from '../Graphql/apolloClient';
import { CONTENT_HUB_QUERY, DELETE_CONTENT_MUTATION } from '../Graphql/Queries';
import { generateMockData } from '../Graphql/MockData';
import {
  Image,
  Video,
  FileText,
  Download,
  Trash2,
  Eye,
  Search,
  Filter,
  Grid,
  List,
  ArrowLeft,
  X,
  Cloud,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import logger from '@/utils/logger';

// Interfaces
interface ContentItem {
  id: string;
  title: string;
  description?: string;
  category: string;
  type: 'image' | 'video' | 'document';
  fileUrl: string;
  thumbnailUrl?: string;
  fileSize: string;
  uploadDate: string;
  tags: string[];
  isStarred: boolean;
  status: 'active' | 'draft' | 'archived';
  usageCount: number;
  campaigns: string[];
  mimeType: string;
  resolution?: string;
  duration?: string;
  filePath: string;
}

interface UploadedFile {
  id: string;
  name: string;
  fileName: string;
  type: 'image' | 'video';
  size: string;
  uploadDate: string;
  thumbnailUrl?: string;
  status: 'pending';
  duration?: string;
  resolution: string;
  mimeType: string;
  file?: File;
  filePath?: string;
  totalPages?: number;
}

interface ContentHubItem {
  id: string;
  fileName: string;
  mimeType: string;
  resolution: string;
  thumbnail?: string;
  duration?: string;
  filePath: string;
  totalPages?: number;
}

interface FilterValues {
  resolution?: string[];
  mimeType?: string;
}

// Utility Functions
function formatDuration(seconds: string | undefined): string {
  if (!seconds || seconds === 'N/A') return 'N/A';
  const secs = parseFloat(seconds);
  if (isNaN(secs)) return 'N/A';
  const mins = Math.floor(secs / 60);
  const remainingSecs = Math.floor(secs % 60);
  return `${mins} min${mins !== 1 ? 's' : ''} ${remainingSecs} sec${remainingSecs !== 1 ? 's' : ''}`;
}

// Helper to get creative type label from mimeType or fileName
function getCreativeTypeLabel(mimeType: string, fileName?: string): string {
  if (!mimeType) return 'Unknown';
  if (mimeType.startsWith('image/')) {
    // Display as Photo/png, Photo/gif, Photo/jpeg, etc.
    return `Photo/${mimeType.split('/')[1]}`;
  }
  if (mimeType.startsWith('video/')) {
    // Display as Video/mp4, Video/avi, etc.
    return `Video/${mimeType.split('/')[1]}`;
  }
  if (mimeType === 'application/pdf') return 'Document/pdf';
  if (mimeType === 'application/msword' || mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') return 'Document/word';
  if (mimeType === 'application/vnd.ms-excel' || mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') return 'Document/excel';
  return 'Document';
}

// Helper to get icon from mimeType
function getCreativeTypeIcon(mimeType: string) {
  if (!mimeType) return <FileText className="h-4 w-4 text-emerald-500" />;
  if (mimeType.startsWith('image/')) {
    if (mimeType === 'image/gif') return <Image className="h-4 w-4 text-yellow-500" />;
    return <Image className="h-4 w-4 text-emerald-500" />;
  }
  if (mimeType.startsWith('video/')) return <Video className="h-4 w-4 text-emerald-500" />;
  return <FileText className="h-4 w-4 text-emerald-500" />;
}

// Get supported file formats from config
function getSupportedFormats() {
  const creativesConfig = configJson.find((section: any) => section.name.toLowerCase().includes("creatives"));
  if (!creativesConfig) return { formats: [], mimeTypes: [], extensions: [], labels: [] };
  
  const supportedFormats = creativesConfig.configurations.filter((config: any) => config.default === true);
  
  const formatMap: { [key: string]: { mimeTypes: string[], extensions: string[] } } = {
    jpeg: { mimeTypes: ['image/jpeg'], extensions: ['.jpg', '.jpeg'] },
    png: { mimeTypes: ['image/png'], extensions: ['.png'] },
    gif: { mimeTypes: ['image/gif'], extensions: ['.gif'] },
    mp4: { mimeTypes: ['video/mp4'], extensions: ['.mp4'] },
    mov: { mimeTypes: ['video/quicktime'], extensions: ['.mov'] },
    avi: { mimeTypes: ['video/x-msvideo', 'video/avi'], extensions: ['.avi'] },
    webm: { mimeTypes: ['video/webm'], extensions: ['.webm'] },
  };
  
  const mimeTypes: string[] = [];
  const extensions: string[] = [];
  const labels: string[] = [];
  
  supportedFormats.forEach((format: any) => {
    const key = format.key.toLowerCase();
    if (formatMap[key]) {
      mimeTypes.push(...formatMap[key].mimeTypes);
      extensions.push(...formatMap[key].extensions);
      labels.push(format.label);
    }
  });
  
  return { formats: supportedFormats, mimeTypes, extensions, labels };
}

// Validate if a file is supported
function isFileSupported(file: File): boolean {
  const { mimeTypes, extensions } = getSupportedFormats();
  const fileName = file.name.toLowerCase();
  const fileExtension = '.' + fileName.split('.').pop();
  
  // Check by MIME type first
  if (mimeTypes.includes(file.type)) return true;
  
  // Check by extension as fallback
  if (extensions.includes(fileExtension)) return true;
  
  return false;
}

interface contentHubProps {
  base_path?: string;
}
const ContentHub = ({ base_path }: contentHubProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ContentItem | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalContentSize, setTotalContentSize] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [contentHubList, setContentHubList] = useState<UploadedFile[]>([]);
  const [availableResolutions, setAvailableResolutions] = useState<string[]>([]);
  const [availableMimeTypes, setAvailableMimeTypes] = useState<string[]>([]);
  const [allResolutions, setAllResolutions] = useState<string[]>([]);
  const [allMimeTypes, setAllMimeTypes] = useState<string[]>([]);
  const [uploadProgress, setUploadProgress] = useState<number[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [filters, setFilters] = useState<FilterValues>({});
  const [tempFilters, setTempFilters] = useState<FilterValues>({});
  const [openSections, setOpenSections] = useState<string[]>([]);
  // Add state to track if "Select All" was used globally
  const [isGlobalSelectAll, setIsGlobalSelectAll] = useState(false);
  const [invalidFileIndices, setInvalidFileIndices] = useState<number[]>([]);

  const token = localStorage.getItem('authToken');
  const companyId = import.meta.env.VITE_APP_COMPANY_ID || '';
  const userId = localStorage.getItem('userId');

  // Debug authentication data
  logger.log('Auth Debug:', {
    token: token ? 'exists' : 'missing',
    companyId,
    userId: userId ? 'exists' : 'missing',
    mockDataEnabled: import.meta.env.VITE_APP_MOCK_DATA
  });

  const [deleteContent] = useMutation(DELETE_CONTENT_MUTATION);

  const transformToContentItem = (items: UploadedFile[]): ContentItem[] => {
    return items.map(item => ({
      id: item.id,
      title: item.fileName,
      description: `${item.type} content`,
      category: item.type === 'image' ? 'Marketing Materials' : 'Video Content',
      type: item.mimeType.startsWith('image') ? 'image' : item.mimeType.startsWith('video') ? 'video' : 'document',
      fileUrl: item.filePath || '',
      thumbnailUrl: item.thumbnailUrl,
      fileSize: item.size,
      uploadDate: item.uploadDate,
      tags: [],
      isStarred: false,
      status: 'active' as const,
      usageCount: 0,
      campaigns: [],
      mimeType: item.mimeType,
      resolution: item.resolution,
      duration: item.duration,
      filePath: item.filePath || '',
    }));
  };

  const transformToUploadedFile = (items: ContentHubItem[]): UploadedFile[] => {
    return items.map(item => ({
      id: item.id,
      name: item.fileName.split('.')[0],
      fileName: item.fileName,
      type: item.mimeType.startsWith('image') ? 'image' : 'video',
      mimeType: item.mimeType,
      size: 'Unknown',
      uploadDate: new Date().toLocaleDateString(),
      thumbnailUrl: item.thumbnail,
      status: 'pending',
      duration: item.duration || 'N/A',
      resolution: item.resolution || 'N/A',
      filePath: item.filePath,
      totalPages: item.totalPages,
    }));
  };

  const fetchAllItemIds = async (searchValue: string = searchQuery): Promise<string[]> => {
    const useMockData = import.meta.env.VITE_APP_MOCK_DATA === 'true';

    try {
      if (useMockData) {
        interface MockResponse {
          data: {
            contentHub: ContentHubItem[];
          };
        }

        const mockResponse = (await generateMockData(CONTENT_HUB_QUERY)) as MockResponse;
        let allData = transformToUploadedFile(mockResponse.data.contentHub);

        // Apply current filters to get all matching items
        let filteredData = allData.filter((item) => {
          const searchLower = searchValue.toLowerCase();
          const matchesSearch = !searchLower || item.fileName.toLowerCase().includes(searchLower);
          const matchesResolution = !filters.resolution || filters.resolution.includes(item.resolution);
          const matchesMimeType = !filters.mimeType || filters.mimeType.includes(item.mimeType);
          return matchesSearch && matchesResolution && matchesMimeType;
        });

        return filteredData.map(item => item.id);
      } else {
        // For API, fetch all pages
        const variables = {
          accessToken: token || 'dummy-token',
          page: 0,
          size: 10000, // Large number to get all items
          metadatatype: 'LMX_CREATIVE',
          metadatacampaign: false,
          companyId: companyId || import.meta.env.VITE_APP_COMPANY_ID,
          creatives: true,
          userId: userId || 'dummy-user',
          sort: 'lastModifiedDate,desc',
          fileName: searchValue || '',
          resolution: filters.resolution ? filters.resolution.join(',') : '',
          mimeType: filters.mimeType || '',
        };

        const { data } = await client.query({
          query: CONTENT_HUB_QUERY,
          variables,
          fetchPolicy: 'network-only',
        });

        const contentHub = transformToUploadedFile(data.contentHub || []);
        return contentHub.map(item => item.id);
      }
    } catch (error) {
      logger.error('Error fetching all item IDs:', error);
      return [];
    }
  };

  const extractUniqueValues = (data: UploadedFile[]) => {
    const resolutions = Array.from(new Set(data.map(item => item.resolution)))
      .filter(resolution => resolution && resolution !== 'N/A');
    const mimeTypes = Array.from(new Set(data.map(item => item.mimeType)))
      .filter(mimeType => mimeType);
    return { resolutions, mimeTypes };
  };

  const fetchContentHubList = async (page: number = currentPage, searchValue: string = searchQuery) => {
    // Use environment variable to determine mock data usage
    const useMockData = import.meta.env.VITE_APP_MOCK_DATA === 'true';

    // For testing, let's try the API call even without full auth if we have a company ID
    const canTryAPI = !useMockData && (token || companyId);

    if (!useMockData && !canTryAPI) {
      toast({
        title: "Info",
        description: "No authentication found, switching to mock data for demo purposes.",
        variant: "default",
      });
      // Fall back to mock data if no auth
      const mockResponse = (await generateMockData(CONTENT_HUB_QUERY)) as any;
      let allData = transformToUploadedFile(mockResponse.data.contentHub);
      setContentHubList(allData.slice(0, 10));
      setTotalPages(1);
      // Set all possible filter options
      const { resolutions, mimeTypes } = extractUniqueValues(allData);
      setAllResolutions(resolutions as string[]);
      setAllMimeTypes(mimeTypes as string[]);
      setAvailableResolutions(resolutions as string[]);
      setAvailableMimeTypes(mimeTypes as string[]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (useMockData) {
        interface MockResponse {
          data: {
            contentHub: ContentHubItem[];
          };
        }

        const mockResponse = (await generateMockData(CONTENT_HUB_QUERY)) as MockResponse;
        let allData = transformToUploadedFile(mockResponse.data.contentHub);

        // Set all possible filter options only if not already set
        if (allResolutions.length === 0 && allMimeTypes.length === 0) {
          const { resolutions, mimeTypes } = extractUniqueValues(allData);
          setAllResolutions(resolutions as string[]);
          setAllMimeTypes(mimeTypes as string[]);
        }

        let filteredData = allData.filter((item) => {
          const searchLower = searchValue.toLowerCase();
          const matchesSearch = !searchLower || item.fileName.toLowerCase().includes(searchLower);
          const matchesResolution = !filters.resolution || filters.resolution.includes(item.resolution);
          const matchesMimeType = !filters.mimeType || filters.mimeType.includes(item.mimeType);
          return matchesSearch && matchesResolution && matchesMimeType;
        });

        const itemsPerPage = 12;
        const start = (page - 1) * itemsPerPage;
        const paginatedData = filteredData.slice(start, start + itemsPerPage);

        setContentHubList(paginatedData);
        // Update available filters based on current filtered data
        const { resolutions, mimeTypes } = extractUniqueValues(paginatedData);
        setAvailableResolutions(resolutions as string[]);
        setAvailableMimeTypes(mimeTypes as string[]);

        const calculatedPages = Math.ceil(filteredData.length / itemsPerPage) || 1;
        setTotalPages(calculatedPages);
        setTotalContentSize(filteredData.length);
      } else {
        // Try API call
        const variables = {
          accessToken: token || 'dummy-token',
          page: page - 1,
          size: 12,
          metadatatype: 'LMX_CREATIVE',
          metadatacampaign: false,
          companyId: companyId || import.meta.env.VITE_APP_COMPANY_ID,
          creatives: true,
          userId: userId || 'dummy-user',
          sort: 'lastModifiedDate,desc',
          fileName: searchValue || '',
          resolution: filters.resolution ? filters.resolution.join(',') : '',
          mimeType: filters.mimeType || '',
        };

        logger.log('GraphQL Query Variables:', variables);
        logger.log('Making API call to:', GRAPHQL_URI);
        logger.log('Auth token exists:', !!token);

        const { data } = await client.query({
          query: CONTENT_HUB_QUERY,
          variables,
          fetchPolicy: 'network-only',
        });

        logger.log('API Response:', data);

        const contentHub = transformToUploadedFile(data.contentHub || []);
        setContentHubList(contentHub);
        const totalPagesFromApi = data.contentHub[0]?.totalPages ?? 1;
        const totalSizeFromApi = data.contentHub[0]?.contentSize ?? data.contentHub.length;
        setTotalPages(totalPagesFromApi);
        setTotalContentSize(totalSizeFromApi);

        // Set all possible filter options only if not already set
        if (allResolutions.length === 0 && allMimeTypes.length === 0) {
          const unfilteredVariables = {
            ...variables,
            resolution: '',
            mimeType: '',
            page: 0,
            size: 1000, // Fetch a large number to get all possible values
          };
          const { data: unfilteredData } = await client.query({
            query: CONTENT_HUB_QUERY,
            variables: unfilteredVariables,
            fetchPolicy: 'network-only',
          });
          const unfilteredContent = transformToUploadedFile(unfilteredData.contentHub || []);
          const { resolutions, mimeTypes } = extractUniqueValues(unfilteredContent);
          setAllResolutions(resolutions as string[]);
          setAllMimeTypes(mimeTypes as string[]);
        }

        // Update available filters based on current filtered data
        const { resolutions, mimeTypes } = extractUniqueValues(contentHub);
        setAvailableResolutions(resolutions as string[]);
        setAvailableMimeTypes(mimeTypes as string[]);
      }
    } catch (err: any) {
      logger.error('Content Hub fetch error:', err);
      let errorMessage = 'An unexpected error occurred while fetching content';

      if (err.networkError) {
        errorMessage = `Network error: ${err.networkError.message}`;
      } else if (err.graphQLErrors && err.graphQLErrors.length > 0) {
        errorMessage = `GraphQL error: ${err.graphQLErrors[0].message}`;
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContentHubList(currentPage, searchQuery);
  }, [currentPage, filters]);

  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    // Don't clear selected items - maintain selection across pagination
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    
    const currentFilesCount = selectedFiles.length;
    const newInvalidIndices: number[] = [];
    
    files.forEach((file, index) => {
      if (!isFileSupported(file)) {
        newInvalidIndices.push(currentFilesCount + index);
      }
    });
    
    if (newInvalidIndices.length > 0) {
      const supportedFormats = getSupportedFormats().labels.join(', ');
      toast({
        title: "Some files have unsupported formats",
        description: `Please remove invalid files before uploading. Only ${supportedFormats} formats are supported.`,
        variant: "destructive",
      });
    }
    
    setInvalidFileIndices(prev => [...prev, ...newInvalidIndices]);
    setSelectedFiles(prev => [...prev, ...files]);
    setUploadProgress(prev => [...prev, ...new Array(files.length).fill(0)]);
    
    // Reset input value to allow selecting the same file again
    event.target.value = '';
  };

  const handleViewItem = (item: ContentItem) => {
    setSelectedItem(item);
    setViewModalOpen(true);
  };

  const handleDeleteItem = async (item: ContentItem) => {
    logger.log('Delete item called:', item.id);

    // Check if we're using mock data
    const useMockData = import.meta.env.VITE_APP_MOCK_DATA === 'true';

    try {
      if (useMockData) {
        // Handle mock data delete
        logger.log('Deleting from mock data:', item.id);
        const updatedList = contentHubList.filter(hubItem => hubItem.id !== item.id);
        setContentHubList(updatedList);

        toast({
          title: "Success",
          description: `${item.title} has been deleted`,
        });
      } else {
        // Handle real API delete
        logger.log('Deleting via API:', item.id);
        const response = await deleteContent({
          variables: { accessToken: token, contentIds: [item.id] },
        });

        const message = response.data.deleteContent[0].message;
        if (message.startsWith('error.')) {
          toast({
            title: "Error",
            description: "Internal server error",
            variant: "destructive",
          });
        } else {
          toast({
            title: "Success",
            description: `${item.title} has been deleted`,
          });
          fetchContentHubList(currentPage);
        }
      }
    } catch (error: any) {
      logger.error('Delete error:', error);
      toast({
        title: "Error",
        description: "Failed to delete content",
        variant: "destructive",
      });
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedItems.length === 0) return;

    logger.log('Delete selected called:', selectedItems);

    // Check if we're using mock data
    const useMockData = import.meta.env.VITE_APP_MOCK_DATA === 'true';

    try {
      if (useMockData) {
        // Handle mock data bulk delete
        logger.log('Bulk deleting from mock data:', selectedItems);
        const updatedList = contentHubList.filter(hubItem => !selectedItems.includes(hubItem.id));
        setContentHubList(updatedList);
        setSelectedItems([]);

        toast({
          title: "Success",
          description: `${selectedItems.length} item(s) have been deleted`,
        });
      } else {
        // Handle real API bulk delete
        logger.log('Bulk deleting via API:', selectedItems);
        const response = await deleteContent({
          variables: { accessToken: token, contentIds: selectedItems },
        });

        const hasErrors = response.data.deleteContent.some((result: any) =>
          result.message.startsWith('error.')
        );

        if (hasErrors) {
          toast({
            title: "Error",
            description: "Some items could not be deleted",
            variant: "destructive",
          });
        } else {
          toast({
            title: "Success",
            description: `${selectedItems.length} item(s) have been deleted`,
          });
          setSelectedItems([]);
          fetchContentHubList(currentPage);
        }
      }
    } catch (error: any) {
      logger.error('Bulk delete error:', error);
      toast({
        title: "Error",
        description: "Failed to delete selected items",
        variant: "destructive",
      });
    }
  };

  const handleDownload = async (item: ContentItem) => {
    const downloadUrl = item.fileUrl || item.filePath;
    
    if (!downloadUrl) {
      toast({
        title: "Error",
        description: "File URL not available for download",
        variant: "destructive",
      });
      return;
    }

    try {
      toast({
        title: "Downloading",
        description: `Starting download of ${item.title}...`,
      });

      // Extract filename from URL or use item title
      const urlParts = downloadUrl.split('/');
      const originalFilename = urlParts[urlParts.length - 1];
      const fileExtension = originalFilename.split('.').pop();
      const filename = item.title.includes('.') ? item.title : `${item.title}.${fileExtension}`;

      // Fetch the file with CORS enabled
      const response = await fetch(downloadUrl, {
        method: 'GET',
        mode: 'cors',
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.status} ${response.statusText}`);
      }

      // Convert to blob
      const blob = await response.blob();
      
      // Create blob URL
      const blobUrl = window.URL.createObjectURL(blob);
      
      // Create temporary anchor element to trigger download
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the blob URL after a short delay
      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
      }, 100);

      toast({
        title: "Success",
        description: `${item.title} downloaded successfully`,
      });

    } catch (error: any) {
      logger.error('Download error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to download file",
        variant: "destructive",
      });
    }
  };

  const handleUpload = async () => {
    const uploadUrl = GRAPHQL_URI;
    if (!uploadUrl) {
      toast({
        title: "Error",
        description: "Upload endpoint is empty. Please set GRAPHQL_URI in apolloClient.ts.",
        variant: "destructive",
      });
      return;
    }

    if (!token || !userId || !companyId) {
      toast({
        title: "Error",
        description: "Missing authentication data.",
        variant: "destructive",
      });
      return;
    }

    if (selectedFiles.length === 0) {
      toast({
        title: "Error",
        description: "Please select at least one file to upload",
        variant: "destructive",
      });
      return;
    }

    setIsUploading(true);

    try {
      let allSuccess = true;
      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];

        try {
          // Simulate progress for visual feedback
          const progressInterval = setInterval(() => {
            setUploadProgress(prev => {
              const newProgress = [...prev];
              if (newProgress[i] < 90) {
                newProgress[i] += Math.random() * 20;
              }
              return newProgress;
            });
          }, 100);

          const operations = JSON.stringify({
            query: `
              mutation UploadContent(
                $accessToken: String!,
                $file: Upload!,
                $mediaOwnerId: String!,
                $filetype: String!,
                $source: String!,
                $userId: String!
              ) {
                uploadcontent(
                  accessToken: $accessToken,
                  file: $file,
                  mediaOwnerId: $mediaOwnerId,
                  filetype: $filetype,
                  source: $source,
                  userId: $userId
                ) {
                  success
                  message
                }
              }
            `,
            variables: {
              accessToken: token,
              file: null,
              mediaOwnerId: companyId,
              filetype: 'LMX_CREATIVE',
              source: 'LMX_COMMERCE',
              userId: userId || ''
            }
          });

          const map = JSON.stringify({
            "0": ["variables.file"]
          });

          const graphqlFormData = new FormData();
          graphqlFormData.append('operations', operations);
          graphqlFormData.append('map', map);
          graphqlFormData.append('0', file);

          const response = await fetch(GRAPHQL_URI, {
            method: 'POST',
            body: graphqlFormData,
            headers: {
              'Accept': 'application/json',
            },
          });

          clearInterval(progressInterval);

          // Complete the progress
          setUploadProgress(prev => {
            const newProgress = [...prev];
            newProgress[i] = 100;
            return newProgress;
          });

          if (!response.ok) {
            throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
          }

          const jsonResponse = await response.json();
          if (jsonResponse.data?.uploadcontent) {
            const { success, message } = jsonResponse.data.uploadcontent;
            if (success) {
              toast({
                title: "Success",
                description: `Successfully uploaded ${file.name}`,
              });
            } else {
              allSuccess = false;
              throw new Error(message || 'Upload failed');
            }
          } else if (jsonResponse.errors) {
            allSuccess = false;
            throw new Error(jsonResponse.errors[0]?.message || 'Upload failed');
          }
        } catch (uploadError: any) {
          toast({
            title: "Error",
            description: `Failed to upload ${file.name}: ${uploadError.message}`,
            variant: "destructive",
          });
          allSuccess = false;
          continue;
        }
      }

      if (allSuccess) {
        setTimeout(() => {
          setUploadModalOpen(false);
          setSelectedFiles([]);
          setUploadProgress([]);
          setIsUploading(false);
          fetchContentHubList(1);
        }, 1000);
      } else {
        setIsUploading(false);
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: `Upload error: ${error.message}`,
        variant: "destructive",
      });
      setIsUploading(false);
    }
  };

  // Helper to format resolution as '1080px x 1920px'
  function formatResolutionPx(resolution?: string) {
    if (!resolution) return 'N/A';
    const match = resolution.match(/^(\d+)\s*[x×]\s*(\d+)$/i);
    if (match) {
      return `${match[1]}px x ${match[2]}px`;
    }
    return resolution;
  }

  const getFileTypeIcon = (type: string) => {
    switch (type) {
      case 'image':
        return <Image className="h-5 w-5" />;
      case 'video':
        return <Video className="h-5 w-5" />;
      case 'document':
        return <FileText className="h-5 w-5" />;
      default:
        return <FileText className="h-5 w-5" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'draft':
        return 'bg-yellow-100 text-yellow-800';
      case 'archived':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleFiltersChange = async () => {
    setFilters(tempFilters);
    setCurrentPage(1);
    setFiltersOpen(false);
    
    // If global select all was active, update selection for new filter criteria
    if (isGlobalSelectAll) {
      const allIds = await fetchAllItemIds(searchQuery);
      setSelectedItems(allIds);
    }
  };

  const toggleSection = (section: string) => {
    setOpenSections(prev => {
      if (prev.includes(section)) {
        return prev.filter(s => s !== section);
      } else {
        return [...prev, section];
      }
    });
  };

  const toggleArrayFilter = (filterKey: keyof FilterValues, value: string) => {
    setTempFilters(prev => {
      const currentArray = (prev[filterKey] as string[]) || [];
      const newArray = currentArray.includes(value)
        ? currentArray.filter((item: string) => item !== value)
        : [...currentArray, value];

      return {
        ...prev,
        [filterKey]: newArray.length > 0 ? newArray : undefined
      };
    });
  };

  const toggleRadioFilter = (filterKey: keyof FilterValues, value: string) => {
    setTempFilters(prev => {
      return {
        ...prev,
        [filterKey]: value === '' ? undefined : value
      };
    });
  };

  useEffect(() => {
    if (filtersOpen) {
      const sectionsToOpen: string[] = [];
      if (filters.resolution && filters.resolution.length > 0) {
        sectionsToOpen.push('resolution');
      }
      if (filters.mimeType) {
        sectionsToOpen.push('mimeType');
      }
      // Initialize tempFilters with current filters when modal opens
      setTempFilters(filters);
      // Only update openSections if the modal was just opened to avoid overriding manual toggles
      if (openSections.length === 0) {
        setOpenSections(sectionsToOpen);
      }
    } else {
      setOpenSections([]);
    }
  }, [filtersOpen, filters]);

  const currentContent = transformToContentItem(contentHubList);

  return (
    <div className="min-h-screen bg-gray-50">
      <style>{`
        .filter-section-btn {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
          padding: 0.75rem;
          background-color: transparent;
          transition: background-color 0.15s ease;
        }

        .filter-section-btn:hover {
          background-color: #f9fafb;
        }

        .filter-item {
          display: flex;
          align-items: center;
          padding: 0.5rem;
          border-radius: 4px;
          transition: background-color 0.15s ease;
        }

        .filter-item:hover {
          background-color: #f9fafb;
        }
      `}</style>

      <div className="max-w-[95vw] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header Section */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-emerald-600">Content Hub</h1>
            <Button
              variant="outline"
              onClick={() => navigate("/my-campaigns")}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Campaigns
            </Button>
          </div>
          <div className="flex items-center gap-4 mb-8">
            {/* <h1 className="text-2xl font-bold text-emerald-600">Content Hub</h1> */}
            <p className="text-gray-600">Centralized library to upload, organize, and access all media assets</p>

            {import.meta.env.VITE_APP_MOCK_DATA === 'true' && (
              <Badge variant="outline" className="text-emerald-600 border-emerald-200">
                Using Mock Data
              </Badge>
            )}
          </div>
        </div>

        {/* Search and Actions Bar */}
        <div className="border border-gray-200 rounded-lg bg-white px-4 py-4 mb-3 flex flex-col sm:flex-row gap-4 items-center justify-between shadow-sm">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />

            <Input
              type="text"
              placeholder="Search for your Creatives"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={async (e) => {
                if (e.key === 'Enter') {
                  setCurrentPage(1);
                  fetchContentHubList(1, searchQuery);
                  
                  // If global select all was active, update selection for new search criteria
                  if (isGlobalSelectAll) {
                    const allIds = await fetchAllItemIds(searchQuery);
                    setSelectedItems(allIds);
                  }
                }
              }}
              className="w-full h-12 pl-10 pr-10 px-5 bg-white border-2 border-gray-200 rounded-lg text-gray-900 placeholder:text-gray-400 placeholder:font-normal focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 focus:outline-none transition-all duration-200 hover:border-gray-300 shadow-sm text-sm"
            />
            {searchQuery && (
              <Button
                variant="ghost"
                size="sm"
                onClick={async () => {
                  setSearchQuery('');         // Clear the search input
                  setCurrentPage(1);          // Reset to first page
                  fetchContentHubList(1, ''); // Fetch full unfiltered list
                  
                  // If global select all was active, update selection for cleared search
                  if (isGlobalSelectAll) {
                    const allIds = await fetchAllItemIds('');
                    setSelectedItems(allIds);
                  }
                }}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0 hover:bg-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200"
                title="Clear search"
              >
                <X className="h-3 w-3 text-gray-400" />
              </Button>
            )}
          </div>


          <div className="flex items-center gap-3">
            <Button
              onClick={() => setUploadModalOpen(true)}
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-md font-medium"
            >
              Upload
            </Button>
            <Button
              variant="outline"
              onClick={() => setFiltersOpen(true)}
              className="border-gray-300 text-gray-700 px-4 py-2 rounded-md"
            >
              <Filter className="h-4 w-4 mr-2" />
              Filters
            </Button>

            {/* View Mode Toggle */}
            <div className="flex items-center border border-gray-300 rounded-md">
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
                className={`rounded-none rounded-l-md ${viewMode === 'list' ? 'bg-emerald-600 text-white' : 'text-gray-700'}`}
              >
                <List className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === 'grid' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('grid')}
                className={`rounded-none rounded-r-md ${viewMode === 'grid' ? 'bg-emerald-600 text-white' : 'text-gray-700'}`}
              >
                <Grid className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Creatives Section */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-gray-900">Creatives</h2>
            <div className="flex items-center gap-3">
                            {selectedItems.length > 0 && selectedItems.some(id => currentContent.some(item => item.id === id)) && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-white border-red-300 hover:bg-red-700 bg-red-600"
                  onClick={handleDeleteSelected}
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete All
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                className={`px-4 py-2 rounded-md font-medium bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-600
                }`}
                onClick={async () => {
                  if (isGlobalSelectAll) {
                    // Deselect all globally
                    setSelectedItems([]);
                    setIsGlobalSelectAll(false);
                  } else {
                    // Select all globally (across all pages and current filters)
                    const allIds = await fetchAllItemIds(searchQuery);
                    setSelectedItems(allIds);
                    setIsGlobalSelectAll(true);
                  }
                }}
                disabled={currentContent.length === 0}
              >
                {isGlobalSelectAll ? 'Deselect All' : 'Select All'}
              </Button>
            </div>
          </div>
        </div>

        {/* Content Display */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-emerald-100 mb-4">
              <div className="w-4 h-4 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
            <p className="text-gray-600">Loading creatives...</p>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <div className="max-w-md mx-auto">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <X className="h-8 w-8 text-red-600" />
              </div>
              <h5 className="text-lg font-medium text-gray-900 mb-2">Error Loading Creatives</h5>
              <p className="text-gray-500 text-sm mb-4">{error}</p>
              <Button
                onClick={() => fetchContentHubList(currentPage, searchQuery)}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                Try Again
              </Button>
            </div>
          </div>
        ) : currentContent.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500">No creatives found.</p>
          </div>
        ) : viewMode === 'list' ? (
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="p-0">
              {/* Table Header */}
              <div className="border-b border-gray-200 bg-gray-50 px-6 py-3">
                <div className="grid grid-cols-12 gap-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="col-span-1 flex items-center">
                  </div>
                  <div className="col-span-4">Creative name</div>
                  <div className="col-span-2 text-center">Creative type</div>
                  <div className="col-span-2 text-center">Duration</div>
                  <div className="col-span-2 text-center">Resolution</div>
                  <div className="col-span-1 text-center">Actions</div>
                </div>
              </div>

              {/* Table Body */}
              <div className="divide-y divide-gray-200">
                {currentContent.map((item) => (
                  <div key={item.id} className="px-6 py-4 hover:bg-gray-50 transition-colors duration-150">
                    <div className="grid grid-cols-12 gap-4 items-center">
                      {/* Checkbox */}
                      <div className="col-span-1">
                        <Checkbox
                          checked={selectedItems.includes(item.id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedItems([...selectedItems, item.id]);
                            } else {
                              setSelectedItems(selectedItems.filter(id => id !== item.id));
                              setIsGlobalSelectAll(false); // Reset global select when individual item is deselected
                            }
                          }}
                        />
                      </div>

                      {/* Creative name with thumbnail */}
                      <div className="col-span-4 flex items-center space-x-3">
                        <div className="flex-shrink-0">
                          {item.thumbnailUrl ? (
                            <img
                              src={item.thumbnailUrl}
                              alt={item.title}
                              className="w-16 h-16 object-cover rounded"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.src = '';
                                target.style.display = 'none';
                                if (target.parentElement) {
                                  target.parentElement.innerHTML = `
                                    <div class="w-16 h-16 bg-gray-100 rounded flex items-center justify-center">
                                      ${getFileTypeIcon(item.type)}
                                    </div>
                                  `;
                                }
                              }}
                            />
                          ) : (
                            <div className="w-16 h-16 bg-gray-100 rounded flex items-center justify-center">
                              {getFileTypeIcon(item.type)}
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate" title={item.title}>{item.title}</p>
                        </div>
                      </div>

                      {/* Creative type (dynamic) */}
                      <div className="col-span-2 flex justify-center">
                        <div className="flex items-center space-x-2">
                          {getCreativeTypeIcon(item.mimeType)}
                          <span className="text-sm text-gray-600">{getCreativeTypeLabel(item.mimeType, item.title)}</span>
                        </div>
                      </div>

                      {/* Duration */}
                      <div className="col-span-2 text-center">
                        <span className="text-sm text-gray-600">
                          {formatDuration(item.duration)}
                        </span>
                      </div>

                      {/* Resolution */}
                      <div className="col-span-2 text-center">
                        <span className="text-sm text-gray-600">
                          {formatResolutionPx(item.resolution)}
                        </span>
                      </div>

                      {/* Actions */}
                      <div className="col-span-1 flex justify-center space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-gray-600 hover:text-gray-800"
                          onClick={() => handleViewItem(item)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDeleteItem(item)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ) : (
          /* Grid View */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {currentContent.map((item) => (
              <Card key={item.id} className="bg-white border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200">
                <CardContent className="p-0">
                  <div className="relative">
                    {/* Thumbnail */}
                    {item.thumbnailUrl ? (
                      <img
                        src={item.thumbnailUrl}
                        alt={item.title}
                        className="w-full h-48 object-cover rounded-t-lg"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = '';
                          target.style.display = 'none';
                          if (target.parentElement) {
                            target.parentElement.innerHTML = `
                              <div class="w-full h-48 bg-gray-100 rounded-t-lg flex items-center justify-center">
                                ${getFileTypeIcon(item.type)}
                              </div>
                            `;
                          }
                        }}
                      />
                    ) : (
                      <div className="w-full h-48 bg-gray-100 rounded-t-lg flex items-center justify-center">
                        {getFileTypeIcon(item.type)}
                      </div>
                    )}

                    {/* Checkbox overlay */}
                    <div className="absolute top-3 left-3">
                      <Checkbox
                        checked={selectedItems.includes(item.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedItems([...selectedItems, item.id]);
                          } else {
                            setSelectedItems(selectedItems.filter(id => id !== item.id));
                            setIsGlobalSelectAll(false); // Reset global select when individual item is deselected
                          }
                        }}
                        className="bg-white border-gray-300"
                      />
                    </div>

                    {/* Actions overlay */}
                    <div className="absolute top-3 right-3 flex space-x-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        className="bg-white/90 hover:bg-white text-gray-600 hover:text-gray-800"
                        onClick={() => handleViewItem(item)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        className="bg-white/90 hover:bg-white text-red-600 hover:text-red-700"
                        onClick={() => handleDeleteItem(item)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Card content */}
                  <div className="p-4">
                    <h3 className="font-medium text-gray-900 mb-2 truncate"
                      title={item.title}>{item.title}</h3>

                    <div className="flex items-center space-x-2 mb-2">
                      {item.type === 'image' ? (
                        <>
                          <Image className="h-4 w-4 text-emerald-500" />
                          <span className="text-sm text-gray-600">Photo/jpeg</span>
                        </>
                      ) : item.type === 'video' ? (
                        <>
                          <Video className="h-4 w-4 text-emerald-500" />
                          <span className="text-sm text-gray-600">Video/mp4</span>
                        </>
                      ) : (
                        <>
                          <FileText className="h-4 w-4 text-emerald-500" />
                          <span className="text-sm text-gray-600">Document</span>
                        </>
                      )}
                    </div>

                    <div className="flex items-center justify-between text-sm text-gray-500">
                      <span>{formatDuration(item.duration)}</span>
                      <span>{formatResolutionPx(item.resolution)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 0 && (
          <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-t mt-6 rounded-lg">
            <div className="flex items-center text-sm text-gray-700">
              <span>
                Showing {((currentPage - 1) * 12) + 1} to {Math.min(currentPage * 12, totalContentSize)} of {totalContentSize} creatives
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="flex items-center space-x-1"
              >
                <ChevronLeft className="h-4 w-4" />
                <span>Previous</span>
              </Button>

              <div className="flex items-center space-x-1">
                {(() => {
                  if (totalPages <= 5) {
                    return Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <Button
                        key={page}
                        variant={currentPage === page ? "default" : "outline"}
                        size="sm"
                        onClick={() => handlePageChange(page)}
                        className={`w-8 h-8 p-0 ${currentPage === page ? 'bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-600' : ''}`}
                      >
                        {page}
                      </Button>
                    ));
                  }

                  const pages = [];

                  // Always show first page
                  pages.push(
                    <Button key={1} variant={currentPage === 1 ? "default" : "outline"} size="sm" onClick={() => handlePageChange(1)} className={`w-8 h-8 p-0 ${currentPage === 1 ? 'bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-600' : ''}`}>1</Button>
                  );

                  // Show dots if needed
                  if (currentPage > 4) pages.push(<span key="dots1" className="px-2 text-gray-500">...</span>);

                  // Show pages around current
                  const start = Math.max(2, currentPage - 1);
                  const end = Math.min(totalPages - 1, currentPage + 1);
                  for (let i = start; i <= end; i++) {
                    if (i !== 1 && i !== totalPages) {
                      pages.push(
                        <Button key={i} variant={currentPage === i ? "default" : "outline"} size="sm" onClick={() => handlePageChange(i)} className={`w-8 h-8 p-0 ${currentPage === i ? 'bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-600' : ''}`}>{i}</Button>
                      );
                    }
                  }

                  // Show dots if needed
                  if (currentPage < totalPages - 3) pages.push(<span key="dots2" className="px-2 text-gray-500">...</span>);

                  // Always show last page
                  if (totalPages > 0) {
                    pages.push(
                      <Button key={totalPages} variant={currentPage === totalPages ? "default" : "outline"} size="sm" onClick={() => handlePageChange(totalPages)} className={`w-8 h-8 p-0 ${currentPage === totalPages ? 'bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-600' : ''}`}>{totalPages}</Button>
                    );
                  }

                  return pages;
                })()}
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="flex items-center space-x-1"
              >
                <span>Next</span>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Filters Modal */}
        {filtersOpen && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <Card className="w-full max-w-md bg-white">
              <CardHeader className="flex flex-row items-center justify-between border-b border-gray-200 pb-4">
                <CardTitle className="text-lg font-semibold">Filters</CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setFiltersOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </Button>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <div className="border-b border-gray-200">
                  <button
                    className="filter-section-btn w-full flex items-center justify-between text-left text-sm font-medium text-gray-900"
                    onClick={() => toggleSection('resolution')}
                  >
                    <span className="flex items-center">
                      <Image className="h-4 w-4 mr-2" /> Resolution
                    </span>
                    <ChevronDown
                      className={`h-4 w-4 transition-transform ${openSections.includes('resolution') ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {openSections.includes('resolution') && (
                    <div className="mt-2 max-h-60 overflow-y-auto">
                      {allResolutions.length > 0 ? (
                        allResolutions.map((resolution) => (
                          <div key={resolution} className="filter-item flex items-center space-x-2">
                            <Checkbox
                              id={`resolution-${resolution}`}
                              checked={tempFilters.resolution?.includes(resolution) || false}
                              onCheckedChange={() => toggleArrayFilter('resolution', resolution)}
                            />
                            <label
                              htmlFor={`resolution-${resolution}`}
                              className="text-sm text-gray-700"
                            >
                              {formatResolutionPx(resolution)}
                            </label>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-gray-500">No resolutions available</p>
                      )}
                    </div>
                  )}
                </div>
                <div className="border-b border-gray-200">
                  <button
                    className="filter-section-btn w-full flex items-center justify-between text-left text-sm font-medium text-gray-900"
                    onClick={() => toggleSection('mimeType')}
                  >
                    <span className="flex items-center">
                      <FileText className="h-4 w-4 mr-2" /> File Type
                    </span>
                    <ChevronDown
                      className={`h-4 w-4 transition-transform ${openSections.includes('mimeType') ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {openSections.includes('mimeType') && (
                    <div className="mt-2 max-h-60 overflow-y-auto">
                      <RadioGroup
                        value={tempFilters.mimeType || ''}
                        onValueChange={(value) => toggleRadioFilter('mimeType', value)}
                      >
                        <div className="filter-item flex items-center space-x-2">
                          <RadioGroupItem value="" id="mime-all" />
                          <label
                            htmlFor="mime-all"
                            className="text-sm text-gray-700 cursor-pointer flex-1"
                          >
                            All Types
                          </label>
                        </div>
                        {allMimeTypes.map((mimeType) => (
                          <div key={mimeType} className="filter-item flex items-center space-x-2">
                            <RadioGroupItem value={mimeType} id={`mime-${mimeType}`} />
                            <label
                              htmlFor={`mime-${mimeType}`}
                              className="text-sm text-gray-700 cursor-pointer flex-1"
                            >
                              {getCreativeTypeLabel(mimeType)}
                            </label>
                          </div>
                        ))}
                      </RadioGroup>
                    </div>
                  )}
                </div>
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setTempFilters({});

                    }}
                    className="flex-1"
                  >
                    Reset
                  </Button>
                  <Button
                    onClick={handleFiltersChange}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    Apply Filters
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* View Modal */}
        {viewModalOpen && selectedItem && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <Card className="w-full max-w-2xl bg-white max-h-[90vh] flex flex-col">
              <CardHeader className="flex flex-row items-center justify-between border-b border-gray-200 pb-4 sticky top-0 bg-white z-10">
                <CardTitle className="text-sm sm:text-base lg:text-lg font-semibold break-words line-clamp-2"
                  title={selectedItem.title}>{selectedItem.title}</CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setViewModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600 flex-shrink-0 ml-2"
                >
                  <X className="h-4 w-4" />
                </Button>
              </CardHeader>
              <div className="overflow-y-auto flex-1">
                <CardContent className="p-6">
                  <div className="space-y-6">
                    <div className="flex justify-center">
                      {selectedItem.type === 'video' ? (
                        <video
                          src={selectedItem.fileUrl}
                          controls
                          className="max-w-2xl max-h-80 rounded-lg object-contain w-full aspect-video"
                          poster={selectedItem.thumbnailUrl}
                        >
                          Your browser does not support the video tag.
                        </video>
                      ) : selectedItem.thumbnailUrl ? (
                        <img
                          src={selectedItem.thumbnailUrl}
                          alt={selectedItem.title}
                          className="max-w-2xl max-h-80 object-contain rounded-lg w-full aspect-video"
                        />
                      ) : (
                        <div className="w-full max-w-2xl aspect-video bg-gray-100 rounded-lg flex items-center justify-center">
                          {getFileTypeIcon(selectedItem.type)}
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <h3 className="font-medium text-gray-900 mb-2">Details</h3>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-500">Type:</span>
                            <span className="text-gray-900">
                              {selectedItem.type === 'image' ? 'Photo/jpeg' :
                                selectedItem.type === 'video' ? 'Video/mp4' : 'Document'}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Size:</span>
                            <span className="text-gray-900">{selectedItem.fileSize}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Resolution:</span>
                            <span className="text-gray-900">{selectedItem.resolution || 'N/A'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Duration:</span>
                            <span className="text-gray-900">{formatDuration(selectedItem.duration)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Upload Date:</span>
                            <span className="text-gray-900">{selectedItem.uploadDate}</span>
                          </div>
                        </div>
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900 mb-2">Usage</h3>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-500">Status:</span>
                            <Badge className={getStatusColor(selectedItem.status)}>
                              {selectedItem.status}
                            </Badge>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Usage Count:</span>
                            <span className="text-gray-900">{selectedItem.usageCount} times</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Category:</span>
                            <span className="text-gray-900">{selectedItem.category}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </div>
              <div className="sticky bottom-0 bg-white border-t border-gray-200 p-6 z-10">
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setViewModalOpen(false)}
                    className="flex-1"
                  >
                    Close
                  </Button>
                  <Button
                    onClick={() => selectedItem && handleDownload(selectedItem)}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Upload Modal */}
        {uploadModalOpen && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <Card className="w-full max-w-lg bg-white max-h-[90vh] flex flex-col">
              <CardHeader className="flex flex-row items-center justify-between border-b border-gray-200 pb-4 sticky top-0 bg-white z-10 rounded-t-lg">
                <CardTitle className="text-lg font-semibold">Upload File</CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setUploadModalOpen(false);
                    setSelectedFiles([]);
                    setUploadProgress([]);
                    setInvalidFileIndices([]);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </Button>
              </CardHeader>
              <CardContent className="p-6 flex-1 overflow-y-auto">
                <div
                  className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    const files = Array.from(e.dataTransfer.files);
                    
                    const newInvalidIndices: number[] = [];
                    
                    files.forEach((file, index) => {
                      if (!isFileSupported(file)) {
                        newInvalidIndices.push(index);
                      }
                    });
                    
                    if (newInvalidIndices.length > 0) {
                      const supportedFormats = getSupportedFormats().labels.join(', ');
                      toast({
                        title: "Some files have unsupported formats",
                        description: `Please remove invalid files before uploading. Only ${supportedFormats} formats are supported.`,
                        variant: "destructive",
                      });
                    }
                    
                    setInvalidFileIndices(newInvalidIndices);
                    setSelectedFiles(files);
                    setUploadProgress(new Array(files.length).fill(0));
                  }}
                >
                  <Cloud className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Drag & drop to upload</h3>
                  <label htmlFor="file-upload" className="cursor-pointer">
                    <p className="text-sm text-emerald-600 mb-4">or browse</p>
                  </label>
                  <p className="text-xs text-gray-500">
                    You can upload multiple files ({getSupportedFormats().labels.join(', ')})
                  </p>

                  {selectedFiles.length > 0 && (
                    <div className="mt-4 space-y-2">
                      {selectedFiles.map((file, index) => {
                        const isInvalid = invalidFileIndices.includes(index);
                        return (
                        <div key={index} className={`flex items-center p-2 rounded ${
                          isInvalid ? 'bg-red-50 border border-red-200' : 'bg-gray-50'
                        }`}>
                          <div className="flex items-center space-x-2 flex-1 min-w-0">
                            <div className={`w-8 h-8 rounded flex items-center justify-center flex-shrink-0 ${
                              isInvalid ? 'bg-red-100' : 'bg-yellow-100'
                            }`}>
                              {isInvalid ? (
                                <X className="h-4 w-4 text-red-600" />
                              ) : (
                                <Image className="h-4 w-4 text-yellow-600" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0 text-left">
                              <div className="flex items-center gap-2">
                                <span className={`text-sm truncate block text-left ${
                                  isInvalid ? 'text-red-700 font-medium' : 'text-gray-700'
                                }`}>{file.name}</span>
                                {isInvalid && (
                                  <Badge variant="destructive" className="text-xs px-1.5 py-0 h-5">
                                    Invalid
                                  </Badge>
                                )}
                              </div>
                              {isInvalid && (
                                <p className="text-xs text-red-600 mt-0.5">Unsupported format</p>
                              )}
                              {!isInvalid && uploadProgress[index] !== undefined && uploadProgress[index] > 0 && (
                                <div className="mt-1 space-y-1">
                                  <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                                    <div
                                      className="h-full bg-emerald-500 transition-all duration-300 ease-out rounded-full"
                                      style={{ width: `${uploadProgress[index]}%` }}
                                    />
                                  </div>
                                  <p className="text-xs text-emerald-600 text-left">{Math.round(uploadProgress[index])}%</p>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex-shrink-0 ml-2">
                            {!isUploading && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:bg-red-100"
                                onClick={() => {
                                  const newFiles = selectedFiles.filter((_, i) => i !== index);
                                  const newProgress = uploadProgress.filter((_, i) => i !== index);
                                  const newInvalidIndices = invalidFileIndices
                                    .filter(i => i !== index)
                                    .map(i => i > index ? i - 1 : i);
                                  
                                  setSelectedFiles(newFiles);
                                  setUploadProgress(newProgress);
                                  setInvalidFileIndices(newInvalidIndices);
                                }}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      )})}
                    </div>
                  )}

                  <input
                    type="file"
                    multiple
                    onChange={handleFileUpload}
                    className="hidden"
                    id="file-upload"
                    accept={getSupportedFormats().extensions.join(',')}
                  />
                </div>
              </CardContent>
              <div className="border-t border-gray-200 p-6 bg-white sticky bottom-0 rounded-b-lg">
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setUploadModalOpen(false);
                      setSelectedFiles([]);
                      setUploadProgress([]);
                      setIsUploading(false);
                      setInvalidFileIndices([]);
                    }}
                    className="flex-1"
                    disabled={isUploading}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleUpload}
                    disabled={selectedFiles.length === 0 || isUploading || invalidFileIndices.length > 0}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    title={invalidFileIndices.length > 0 ? 'Remove invalid files before uploading' : ''}
                  >
                    {isUploading ? 'Uploading...' : invalidFileIndices.length > 0 ? 'Remove Invalid Files' : 'Upload'}
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContentHub;